import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const ss = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export const POUR_TIME = 5.4;
export const GLASS_X = 1.05;
export const TIP_X = -0.7;
export const TIP_Y = 1.3;
const MOUTH = 1.9;
const BASE = -1.84;
const FULL = 1.55;

export const curves = (T) => ({
  tip: ss(0, 0.8, T) * (1 - ss(2.7, 3.5, T)),
  glass: ss(0.05, 0.6, T) * (1 - ss(4.6, 5.3, T)),
  flow: ss(0.7, 0.85, T) * (1 - ss(2.55, 2.75, T)),
  level: ss(0.8, 2.75, T),
  foam: ss(1.4, 3.0, T),
});

export const POUR_COLORS = { original: "#E3A21C", berry: "#8A1540" };

const GLASS_PROFILE = [[0, -1.9], [0.4, -1.9], [0.44, -1.84], [0.5, -1.0], [0.57, -0.15], [0.585, -0.13]].map(([x, y]) => new THREE.Vector2(x, y));

export function Pour({ pour }) {
  const root = useRef();
  const liquid = useRef();
  const foam = useRef();
  const stream = useRef();
  const bubbles = useRef();
  const mats = useRef({});
  const glassGeo = useMemo(() => new THREE.LatheGeometry(GLASS_PROFILE, 64), []);
  const liqGeo = useMemo(() => new THREE.CylinderGeometry(0.52, 0.4, 1, 48).translate(0, 0.5, 0), []);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const seeds = useMemo(() => Array.from({ length: 30 }, () => [(Math.random() - 0.5) * 0.55, Math.random(), (Math.random() - 0.5) * 0.55, 0.25 + Math.random() * 0.5]), []);

  useFrame((state) => {
    const T = state.clock.elapsedTime - pour.current.t0;
    const c = curves(T);
    const m = mats.current;
    root.current.visible = c.glass > 0.01;
    if (!root.current.visible) return;
    root.current.scale.setScalar(0.7 + 0.3 * c.glass);
    root.current.position.y = -0.5 * (1 - c.glass);
    m.glass.opacity = 0.28 * c.glass;
    m.liquid.color.set(pour.current.color);
    m.stream.color.set(pour.current.color);
    m.liquid.opacity = 0.9 * c.glass;
    m.foam.opacity = 0.95 * c.glass;

    const h = Math.max(c.level * FULL, 0.001);
    liquid.current.scale.set(1, h, 1);
    liquid.current.visible = c.level > 0.005;
    foam.current.position.y = BASE + h;
    foam.current.scale.set(1, Math.max(c.foam, 0.01), 1);
    foam.current.visible = c.foam > 0.02;

    const mx = TIP_X * c.tip + MOUTH * Math.sin(2 * c.tip) - GLASS_X;
    const top = TIP_Y * c.tip + MOUTH * Math.cos(2 * c.tip);
    const bottom = BASE + h;
    stream.current.visible = c.flow > 0.01 && top > bottom;
    stream.current.position.set(mx + Math.sin(T * 30) * 0.004, (top + bottom) / 2, 0);
    stream.current.scale.set(c.flow, Math.max(top - bottom, 0.001), c.flow);

    seeds.forEach(([x, o, z, sp], i) => {
      const y = BASE + ((o + T * sp) % 1) * h;
      dummy.position.set(x * (0.75 + 0.25 * c.level), y, z * (0.75 + 0.25 * c.level));
      dummy.scale.setScalar(h > 0.08 ? 0.022 + (i % 3) * 0.006 : 0.0001);
      dummy.updateMatrix();
      bubbles.current.setMatrixAt(i, dummy.matrix);
    });
    bubbles.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group position={[GLASS_X, 0, 0.2]}>
      <group ref={root} visible={false}>
        <mesh geometry={glassGeo} renderOrder={3}>
          <meshPhysicalMaterial ref={(r) => (mats.current.glass = r)} color="#ffffff" transparent opacity={0} roughness={0.04} metalness={0} clearcoat={1} clearcoatRoughness={0.05} envMapIntensity={2.4} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
        <mesh position={[0, -1.87, 0]}>
          <cylinderGeometry args={[0.41, 0.41, 0.06, 48]} />
          <meshPhysicalMaterial color="#ffffff" transparent opacity={0.35} roughness={0.05} clearcoat={1} />
        </mesh>
        <mesh ref={liquid} geometry={liqGeo} position={[0, BASE, 0]} renderOrder={1}>
          <meshPhysicalMaterial ref={(r) => (mats.current.liquid = r)} color="#E3A21C" transparent opacity={0} roughness={0.12} clearcoat={0.6} emissive="#5a3a00" emissiveIntensity={0.25} />
        </mesh>
        <mesh ref={foam} position={[0, BASE, 0]} renderOrder={2}>
          <cylinderGeometry args={[0.535, 0.52, 0.14, 48]} />
          <meshStandardMaterial ref={(r) => (mats.current.foam = r)} color="#FFF6DE" transparent opacity={0} roughness={0.9} />
        </mesh>
        <mesh ref={stream} renderOrder={2}>
          <cylinderGeometry args={[0.07, 0.09, 1, 16, 1, true]} />
          <meshPhysicalMaterial ref={(r) => (mats.current.stream = r)} color="#E3A21C" transparent opacity={0.85} roughness={0.1} clearcoat={1} />
        </mesh>
        <instancedMesh ref={bubbles} args={[null, null, seeds.length]}>
          <sphereGeometry args={[1, 8, 8]} />
          <meshStandardMaterial color="#FFF3C4" emissive="#FFE9A0" emissiveIntensity={0.6} transparent opacity={0.9} />
        </instancedMesh>
      </group>
    </group>
  );
}
